import { templatesApi } from '@/shared/api/transcriptor/templatesApi';
import { debug } from '@/shared/lib';

import { normalizeDocumentType } from '@/entities/templates';
import type { DocumentTemplate } from '@/entities/templates';

const normalizeTemplateName = (name: string): string =>
  name.trim().toLowerCase();

const checkForConflicts = async (
  template: DocumentTemplate,
  organizationId: string,
): Promise<void> => {
  const listResponse = await templatesApi.listDocumentTemplates({
    organization_id: organizationId,
    language: template.language,
  });

  const existingCodes = new Set(
    listResponse.document_templates
      .filter((item) => item.language === template.language)
      .map((item) => item.code),
  );

  const existingNames = new Set(
    listResponse.document_templates
      .filter((item) => item.language === template.language)
      .map((item) => normalizeTemplateName(item.name)),
  );

  if (
    existingCodes.has(template.code) ||
    existingNames.has(normalizeTemplateName(template.name))
  ) {
    throw new Error(
      'Template copy requires explicit conflict resolution for code or name.',
    );
  }
};

export const createOrganizationOwnedTemplateCopy = async (
  template: DocumentTemplate,
  organizationId: string,
): Promise<DocumentTemplate> => {
  if (!organizationId) {
    throw new Error('Organization ID is required to copy this template.');
  }

  await checkForConflicts(template, organizationId);

  const { template: created } = await templatesApi.createDocumentTemplate({
    code: template.code,
    language: template.language,
    type: normalizeDocumentType(template.type),
    name: template.name,
    tab_title: template.tab_title,
    description: template.description ?? undefined,
    sections: template.sections,
    source_document: template.source_document,
    organization_id: organizationId,
    source_kind: 'organization',
    derived_from_kind: template.is_system ? 'system' : undefined,
    derived_from_id: template.id,
  });

  return created;
};

export const replaceSchemaTemplateSeries = async (
  schemaId: string,
  fromSeriesId: string,
  toSeriesId: string,
  organizationId?: string,
): Promise<void> => {
  const schema = await templatesApi.getEncounterSchema(
    schemaId,
    organizationId,
  );

  const replaced = schema.documents.some(
    (documentRef) => documentRef.template_series_id === fromSeriesId,
  );

  const updatedDocuments = schema.documents.map((documentRef) => {
    if (documentRef.template_series_id !== fromSeriesId) {
      return {
        template_series_id: documentRef.template_series_id ?? '',
        inclusion: documentRef.inclusion as 'always' | 'on_demand' | undefined,
      };
    }

    return {
      template_series_id: toSeriesId,
      inclusion: documentRef.inclusion as 'always' | 'on_demand' | undefined,
    };
  });

  if (!replaced) {
    updatedDocuments.push({
      template_series_id: toSeriesId,
      inclusion: 'on_demand' as const,
    });
  }

  await templatesApi.updateEncounterSchema(schemaId, {
    documents: updatedDocuments,
  });
};

export type CloneSystemTemplateResult = {
  createdTemplate: DocumentTemplate;
  schemaUpdated: boolean;
};

type CloneSystemTemplateArgs = {
  template: DocumentTemplate;
  organizationId: string;
  schemaId?: string;
};

export const cloneSystemTemplateForEditing = async (
  args: CloneSystemTemplateArgs,
): Promise<CloneSystemTemplateResult> => {
  const { template, organizationId, schemaId } = args;

  const createdTemplate = await createOrganizationOwnedTemplateCopy(
    template,
    organizationId,
  );

  if (!schemaId || !template.series_id || !createdTemplate.series_id) {
    return {
      createdTemplate,
      schemaUpdated: false,
    };
  }

  try {
    await replaceSchemaTemplateSeries(
      schemaId,
      template.series_id,
      createdTemplate.series_id,
      organizationId,
    );
    return {
      createdTemplate,
      schemaUpdated: true,
    };
  } catch (error) {
    debug('Failed to swap schema template reference to cloned template', error);
    return {
      createdTemplate,
      schemaUpdated: false,
    };
  }
};
